import React, { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { Tabs as FontaineTabs } from '../../components/ui';
import { SettingsContext } from '../../contexts';
import { Visible } from '../../core/components';
import { Tabs as LegacyTabs } from "../../layouts/Default"
import { Lookbooks, ManageListings, Network } from '../organisms';

const getTabs = (isMerchant) => [
  { label: 'Manage Listings', link: '/listings' },
  ...(isMerchant ?
    [] :
    [
      { label: 'Lookbooks', link: '/listings/lookbooks' },
      { label: 'Listings Network', link: '/listings/network' },
    ]
  ),
];

const activeTabFn = (pathname, tab, tabs) => {
  return pathname === tab.link;
}

// `fontaineShell` is opt-in per consumer, not a rename. The template is shared:
// ListingsPage, NetworkPage and LookbooksPage all render it, and only
// ListingsPage passes the flag. The default branch below is the legacy strip
// exactly as it was, so the other two are untouched.
export function ListingsTemplate({ fontaineShell = false }) {
  const { isMerchant, iframeQueryParams } = useContext(SettingsContext);
  const { pathname } = useLocation();

  const tabs = useMemo(() => {
    return getTabs(isMerchant);
  }, [isMerchant]);

  // The legacy strip built each href itself: `tab.link + `?${iframeQueryParams}``.
  // Fontaine's Tabs takes `to` as given, so the same string is built here —
  // trailing `?` and all — keeping the iframe hosts' hrefs byte-identical. The
  // query string is passed through untouched: not re-encoded, not reordered,
  // not coalesced.
  const link = (tab) => `${tab.link}?${iframeQueryParams}`;
  // Left to the location, Tabs would compare a query-bearing `to` against a
  // bare pathname and find no active tab, so the active one is named outright —
  // by the same `pathname === tab.link` rule `activeTabFn` uses. `exact` pins
  // each item to its own route, so the NavLink each one renders as agrees.
  const current = tabs.find((tab) => tab.link === pathname);

  return (
    <>
      {fontaineShell ? (
        <FontaineTabs
          items={tabs.map((tab) => ({ label: tab.label, to: link(tab), exact: true }))}
          activePath={current ? link(current) : undefined}
          variant="underline"
          fullWidth
          aria-label="Listings sections"
        />
      ) : (
        <LegacyTabs tabs={tabs} mobile desktop activeFn={activeTabFn} />
      )}

      <Visible when={pathname === '/listings'}>
        <ManageListings />
      </Visible>

      <Visible when={pathname === '/listings/lookbooks'}>
        <Lookbooks />
      </Visible>

      <Visible when={pathname === '/listings/network'}>
        <Network />
      </Visible>
    </>
  );
}
